import React, {useEffect, useRef} from 'react';
import {useHistory, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {createPayerData, mapInvoiceListToPaymentDocuments, mapPaymentDetails} from "./PaymentFunctions";
import {PaymentRequest} from "../../interfaces/PaymentRequest";
import {getAndClearSessionStorage} from "../../utils";
import {getPaymentResponse} from "../../requests/TokenRequests";
import {
    useCurrencyDecimalLookup,
    useCurrencyDecimals,
    useCurrencyKey
} from "../../selectors/ConfigSelectors";
import {useInvoiceSearchQuery} from "./InvoiceDataManager";
import {getInvoices, makePayment} from "../../actions/InvoiceActions";
import {CACHED_CVV} from "./Payment";
import { RootState } from "../../reducers/RootReducer";
import {
    consumeThreeDSFlowContext,
    handleThreeDSContextError,
    resolveRestoredPaymentOption,
    rehydrateThreeDSAccountContext,
    validateThreeDSFlowContext
} from "../../threeDSFlowContext";
import { AccountType } from "../../interfaces/Accounts";

export function ProcessingPaymentPage(props: {}) {
    const location = useLocation();
    const history = useHistory();
    const search = new URLSearchParams(location.search);
    const accessToken = search.get("id");
    const currencyKey = useCurrencyKey();
    const currencyDecimals = useCurrencyDecimals(currencyKey);
    const dispatch = useDispatch();
    const currentUser = useSelector<any, any>(state => state.default.currentUser);
    const decimalLookup = useCurrencyDecimalLookup();
    const invoiceSearchQuery = useInvoiceSearchQuery();
    const invoices = useSelector<any, any>(store => store.invoice.selected);
    const invoiceDetails = useSelector<any, any>(store => store.invoice.invoiceDetail);
    const primaryAccounts = useSelector<RootState, any>(state => state.user_options.primaryAccounts);
    const secondaryAccounts = useSelector<RootState, any>(state => state.user_options.secondaryAccounts);
    const accountType = useSelector<RootState, any>(state => state.user_options.accountType);
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (!accessToken) {
            history.replace("/");
            return;
        }
        if (hasProcessed.current) {
            return;
        }
        if (Object.keys(primaryAccounts ?? {}).length === 0 || !currentUser) {
            return;
        }

        (async () => {
            hasProcessed.current = true;
            const context = consumeThreeDSFlowContext();
            if (!validateThreeDSFlowContext(context, "invoice-payment")) {
                handleThreeDSContextError(history, "/invoices/pay", "Your 3DS session expired. Please try the payment again.");
                return;
            }

            try {
                const cvv = getAndClearSessionStorage(CACHED_CVV);
                const resolvedAccounts = await rehydrateThreeDSAccountContext(
                    dispatch,
                    context,
                    primaryAccounts,
                    secondaryAccounts,
                    currentUser,
                    accountType,
                );
                const restoredCustomerNumber = accountType === AccountType.SoldTo && resolvedAccounts.secondaryAccount
                    ? resolvedAccounts.secondaryAccount.PrimaryAcct
                    : resolvedAccounts.primaryAccount.PrimaryAcct;
                const restoredPaymentOption = resolveRestoredPaymentOption(
                    context,
                    resolvedAccounts.payerDetails,
                    restoredCustomerNumber,
                );
                let { cardinal } = await getPaymentResponse(accessToken);
                let invoicesToPay = mapInvoiceListToPaymentDocuments(invoices, invoiceDetails);
                let paymentDetails = mapPaymentDetails(
                    restoredPaymentOption,
                    cvv,
                    resolvedAccounts.payerDetails?.address_data,
                    cardinal
                );
                let payerData = createPayerData(restoredCustomerNumber, resolvedAccounts.primaryAccount.CompanyCode);
                let paymentRequest: PaymentRequest = {
                    payer_data: payerData,
                    soldto: {
                        account_number: resolvedAccounts.primaryAccount.PrimaryAcct
                    },
                    payment_detail: paymentDetails,
                    documents: invoicesToPay,
                    currency_key: currencyKey,
                    currency_decimals: currencyDecimals,
                };
                let success: any = await dispatch(makePayment(paymentRequest));
                history.replace('/invoices/payment-status');
                if (success) {
                    dispatch(getInvoices(invoiceSearchQuery, decimalLookup));
                }
            } catch (error: any) {
                hasProcessed.current = false;
                handleThreeDSContextError(history, "/invoices/pay", error?.message ?? "We couldn't restore the original account for this 3DS flow.");
            }
        })();
    }, [accessToken, accountType, currencyDecimals, currencyKey, currentUser, decimalLookup, dispatch, history, invoiceDetails, invoiceSearchQuery, invoices, primaryAccounts, secondaryAccounts]);

    return <div/>;
}
